import Router from 'vue-router';
import ErrorPage from '@src/views/Error';
import MainLayout from '@src/layouts/Main';
import { trackingRoute } from '@src/lib/logs';

export const routes = [
  /**
  * GENERAL
  */
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    components: {
      default: () => import('@/views/Dashboards/Controller'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard-im',
    redirect: '/dashboard',
  },
  {
    name: 'Error',
    path: '/error/:status?',
    component: () => import('@/views/Error'),
    props: true,
  },
  /**
  * AUTH
  */
  {
    name: 'Login',
    path: '/login',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Login'),
    },
  },
  {
    name: 'LoginAdmin',
    path: '/login/admin',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/SSO'),
    },
  },
  {
    name: 'LoginForgot',
    path: '/login/forgot',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/LoginForgot'),
    },
    children: [
      { path: ':encodedEmail/:code' },
    ],
  },
  {
    name: 'LoginSSO',
    path: '/login/confirm',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/SSO'),
    },
    meta: {
      scrollBehavior: false,
    },
  },
  {
    name: 'Register',
    path: '/register',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Register'),
    },
  },
  {
    name: 'RegisterInvite',
    path: '/register/invite/:userInviteID',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Register'),
    },
    props: { layout: false, default: true, focus: true },
    meta: {
      requiresAuth: false,
      pageView: ({ userInviteID }) => ({ userInvite: { id: userInviteID } }),
    },
  },
  {
    name: 'RegisterReferral',
    path: '/register/referral/:userReferrerID',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Register'),
    },
    props: { layout: false, default: true, focus: true },
  },
  {
    name: 'RegisterVerify',
    path: '/register/verify',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/RegisterVerify'),
    },
    children: [
      { name: 'RegisterVerifyEmailCode', path: ':encodedEmail/:code' },
    ],
    meta: {
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'Logout',
    path: '/logout(/confirm)?',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Logout'),
    },
  },
  {
    name: 'LogoutAdmin',
    path: '/logout/admin',
    components: {
      layout: () => import('@/layouts/Minimal'),
      default: () => import('@/views/Auth/Container'),
      focus: () => import('@/views/Auth/Logout'),
    },
  },
  {
    name: 'RegisterQuestions',
    path: '/register/questions',
    component: () => import('@/views/Auth/RegisterQuestions'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: () => ({ user: { self: true } }),
    },
  },

  {
    name: 'Onboarding',
    path: '/onboarding/:stepName?',
    components: {
      layout: MainLayout,
      default: () => import('@/views/onboarding/index'),
    },
    props: {
      layout: false,
      default: true,
    },
    meta: {
      requiresAuth: true,
      pageView: () => ({ user: { self: true } }),
      scrollBehavior: false,
    },
  },
  {
    path: '/register/network',
    redirect: '/onboarding/registernetwork',
  },

  /**
  * SETTINGS
  */
  {
    path: '/settings/:tab?',
    name: 'Settings',
    components: {
      layout: MainLayout,
      default: () => import('@/views/settings/index'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    path: '/campaign-admin',
    name: 'CampaignAdmin',
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Campaigns'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    path: '/company-setup',
    name: 'CompanySetup',
    components: {
      layout: MainLayout,
      default: () => import('@/views/settings/Setup'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    path: '/campaign-admin/:campaignID/stages',
    name: 'EditCampaignStages',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Stages.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/:campaignID/gamification',
    name: 'EditCampaignGamification',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Gamification.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/:campaignID/engagement',
    name: 'EditCampaignEngagement',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Engagement.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/:campaignID/engagement-relative-dates',
    name: 'EditCampaignEngagementRelativeDates',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/EngagementRelativeDates.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  // {
  //   path: '/campaign-admin/:campaignID/engagement2',
  //   name: 'EditCampaignEngagementCalendar',
  //   props: { default: true },
  //   components: {
  //     layout: MainLayout,
  //     default: () => import('@/views/campaign-admin/EngagementCalendar.vue'),
  //   },
  //   meta: {
  //     requiresAuth: true,
  //     requiresAdmin: true,
  //   },
  // },
  {
    path: '/campaign-admin/:campaignID/jobs',
    name: 'EditCampaignJobs',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Jobs.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/:campaignID/content',
    name: 'EditCampaignContent',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Content.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/:campaignID/participants',
    name: 'EditCampaignParticipants',
    props: { default: true },
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/Participants.vue'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/settings/email-setup',
    name: 'EmailSetup',
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/EmailSetup'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    }
  },
  {
    path: '/campaign-admin/campaign-analytics',
    name: 'CampaignAnalytics',
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/CampaignAnalyticsV2'),
    },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true
    }
  },
  {
    path: '/campaign-admin/campaign-analytics-v2',
    name: 'CampaignAnalyticsV2',
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/CampaignAnalyticsV2'),
    },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true
    }
  },
  {
    path: '/campaign-admin/settings/email-signature',
    name: 'EmailSignature',
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/EmailSignature'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/campaign-admin/settings/collective-goal',
    name: 'SettingsCollectiveGoal',
    components: {
      layout: MainLayout,
      default: () => import('@/views/campaign-admin/SettingsCollectiveGoal'),
    },
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    }
  },

  /**
  * USERS
  */
  {
    name: 'Profile',
    path: '/profile',
    component: () => import('@/views/User/EditProfile'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'ProfileEdit',
    path: '/edit-profile',
    component: () => import('@/views/User/Profile'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'UserApplications',
    path: '/user-applications',
    component: () => import('@/views/User/Applications'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: 'UserMentorship',
    path: '/user-mentorship',
    component: () => import('@/views/User/Mentorship'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: 'ProfileSessions',
    path: '/profile/sessions',
    component: () => import('@/views/User/EditSessions'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'ProfileSyncContacts',
    path: '/profile/sync-contacts',
    component: () => import('@/views/User/SyncContacts'),
    props: true,
    meta: {
      requiresAuth: true,
      scrollBehavior: false,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'ProfileAlerts',
    path: '/profile/alerts',
    component: () => import('@/views/User/EditAlerts'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: () => ({ user: { self: true } }),
    },
  },
  {
    name: 'ProfileScheduledPosts',
    path: '/profile/scheduled-posts',
    components: {
      default: () => import('@src/views/User/ScheduledPosts'),
      layout: MainLayout,
    },
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  /**
  * REFERRALS
  */
  {
    path: '/referrals',
    name: 'Referrals',
    component: () => import('@/views/users/referrals'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/referral-requests',
    name: 'ReferralRequests',
    component: () => import('@/views/users/ReferralRequests'),
    meta: {
      requiresAuth: true,
    },
  },

  /**
  * CONTACTS
  */
  {
    path: '/contacts',
    name: 'ContactsIndex',
    component: () => import('@/views/contacts/contactsIndex'),
    meta: {
      requiresAuth: true,
    },
  },

  /**
  * JOBS
  */
  {
    path: '/jobs',
    name: 'JobIndex',
    component: () => import('@/views/jobs/jobsIndex'),
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/internal-jobs',
    name: 'InternalJobIndex',
    component: () => import('@/views/jobs/internalJobsIndex'),
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/campaign-jobs',
    name: 'CampaignJobIndex',
    components: {
      default: () => import('@/views/jobs/CampaignJobsIndex'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/campaign-scheduler/:contentType/:contentIDs',
    name: 'CampaignScheduler',
    components: {
      default: () => import('@/views/User/CampaignScheduler'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/jobs/new',
    name: 'JobNew',
    component: () => import('@/views/jobs/jobEdit'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/roles/new/:projectID',
    name: 'RoleNew',
    component: () => import('@/views/jobs/jobEdit'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/jobs/applications/new',
    name: 'ApplicationNew',
    component: () => import('@/views/job-applications/AddApplication'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/jobs/:jobID/edit',
    name: 'JobEdit',
    props: true,
    component: () => import('@/views/jobs/jobEdit'),
    meta: {
      requiresAuth: true,
      pageView: ({ jobID }) => ({
        job: { id: jobID },
      }),
    },
  },
  {
    path: '/roles/:jobID/edit',
    name: 'RoleEdit',
    component: () => import('@/views/jobs/jobEdit'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  // {
  //   path: '/jobs/view/:jobCode',
  //   name: 'JobView',
  //   component: () => import('@/views/jobs/ShareShow'),
  //   props: true,
  // },
  {
    path: '/jobs/:jobID',
    name: 'JobShow',
    component: () => import('@/views/jobs/jobShow'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: ({ jobID }) => ({
        job: { id: jobID },
      }),
    },
  },
  {
    path: '/campaign-jobs/:jobID',
    name: 'CampaignJobShow',
    component: () => import('@/views/jobs/campaignJobShow'),
    props: true,
    meta: {
      requiresAuth: true,
      pageView: ({ jobID }) => ({
        job: { id: jobID },
      }),
    },
  },
  {
    path: '/internal-jobs/:jobID',
    name: 'InternalJobShow',
    component: () => import('@/views/jobs/InternalJobShow'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/jobs/:jobID/share',
    name: 'JobShowShare',
    component: () => import('@/views/jobs/jobShow'),
    alias: [ '/rs/:jobID' ],
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: '/share/:shareCode',
    name: 'ShareShow',
    component: () => import('@/views/jobs/ShareShow'),
    // @TODO: this is a hotfix for broken links detected on 01/04/2022
    alias: [ '/r/:shareCode', '/e/r/:shareCode' ],
    props: {
      layout: false,
      default: true,
    },
    meta: {
      requiresAuth: false,
      pageView: ({ shareCode }) => ({
        job: { shareCode },
        user: { shareCode },
        referral: { shareCode },
      }),
    },
  },
  {
    path: '/share/:shareCode/all',
    name: 'ShareShowAll',
    component: () => import('@/views/jobs/jobsIndex'),
    alias: [ '/r/:shareCode/all' ],
    props: true,
    meta: {
      requiresAuth: false,
      pageView: ({ shareCode }) => ({
        user: { shareCode },
        referral: { shareCode },
      }),
    },
  },
  {
    path: '/share/:shareCode/:jobID',
    name: 'ShareShowAnother',
    component: () => import('@/views/jobs/ShareShow'),
    alias: [ '/r/:shareCode/:jobID' ],
    props: true,
    meta: {
      requiresAuth: false,
      pageView: ({ jobID, shareCode }) => ({
        job: { id: jobID },
        user: { shareCode },
        referral: { shareCode },
      }),
    },
  },
  // {
  //   path: '/share/:shareCode/:shareMethod',
  //   name: 'ShareRedirect',
  //   component: () => import('@/views/jobs/ShareRedirect'),
  //   props: true,
  // },

  // share redirect
  {
    path: '/sr/j/:jobID/:channel/:campaignID',
    name: 'JobShowShareChannelCampaign',
    component: () => import('@/views/User/ShareRedirect'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/sr/c/:contentShareID/:channel/:campaignID',
    name: 'ContentShowShareChannelCampaign',
    component: () => import('@/views/User/ShareRedirect'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  {
    name: 'EventList',
    path: '/events',
    components: {
      default: () => import('@/views/ReferralJam/EventList'),
      layout: () => import('@src/layouts/ReferralJam'),
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: 'EventPage',
    path: '/events/:eventID',
    props: { default: true, layout: false },
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventPage'),
      layout: () => import(/* webpackChunkName: "referralJam" */'@src/layouts/ReferralJam'),
    },
    meta: {
      scrollBehavior: false,
    },
  },
  {
    name: 'EventWelcome',
    path: '/events/:eventID/welcome',
    props: { default: true, layout: false },
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventWelcome'),
      layout: () => import(/* webpackChunkName: "referralJam" */'@src/layouts/ReferralJam'),
    },
    meta: { requiresAuth: true },
  },
  {
    name: 'EventSyncContacts',
    path: '/events/:eventID/sync-contacts',
    props: { default: true, layout: false },
    components: {
      default: () => import('@/views/ReferralJam/EventSyncContacts'),
      layout: () => import('@src/layouts/ReferralJam'),
    },
    meta: { requiresAuth: true },
  },
  {
    name: 'EventRoom',
    path: '/events/:eventID/session',
    props: { default: true, layout: false },
    components: {
      default: () => import(/* webpackChunkName: "referralJam" */'@/views/ReferralJam/EventRoom'),
      layout: () => import(/* webpackChunkName: "referralJam" */'@src/layouts/ReferralJam'),
    },
    meta: {
      requiresAuth: true,
      headerLinks: [
        { title: 'Rules', modal: 'event-rules' },
        // { title: 'Your Activity', modal: 'event-activity' },
      ],
    },
  },
  {
    name: 'EventSettings',
    path: '/events/:eventID/settings',
    props: { default: true, layout: false },
    components: {
      default: () => import('@/views/ReferralJam/EventSettings'),
      layout: () => import('@src/layouts/ReferralJam'),
    },
    meta: {
      requiresAuth: true,
      headerLinks: [
        { title: 'All Events', to: { name: 'EventList' } },
      ],
    },
  },
  {
    name: 'EventJobShareShow',
    path: '/events/:eventID/job-share/:shareCode',
    alias: [ '/e/r/:eventID/:shareCode' ],
    props: { default: true, layout: false },
    component: () => import('@/views/jobs/ShareShow'),
    meta: {
      pageView: ({ eventID, shareCode }) => ({
        event: { id: eventID },
        job: { shareCode },
        user: { shareCode },
      }),
    },
  },
  {
    name: 'EventContentShareShow',
    path: '/events/:eventID/content-share/:shareCode',
    alias: [ '/e/c/:eventID/:shareCode' ],
    props: true,
    component: () => import('@/views/content-share/ShareShow'),
    // props: { default: true, layout: false },
    // components: {
    //   default: () => import('@/views/jobs/ShareShow'),
    //   layout: () => import('@src/layouts/Minimal'),
    // },
    meta: {
      pageView: ({ eventID, shareCode }) => ({
        event: { id: eventID },
        contentShare: { shareCode },
        user: { shareCode },
      }),
    },
  },

  /**
  * PROJECTS
  */
  {
    path: '/projects',
    name: 'ProjectsIndex',
    component: () => import('@/views/projects/ProjectsIndex'),
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/projects/new',
    name: 'ProjectNew',
    component: () => import('@/views/projects/ProjectEdit'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/projects/:projectID/edit',
    name: 'ProjectEdit',
    props: true,
    component: () => import('@/views/projects/ProjectEdit'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/projects/:projectID',
    name: 'ProjectShow',
    component: () => import('@/views/projects/ProjectShow'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  /**
  * LEADERBOARDS
  */
  {
    path: '/leaderboards',
    name: 'LeaderboardIndex',
    component: () => import('@/views/User/LeaderboardIndex'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/leaderboards/:leaderboardID',
    name: 'LeaderboardShow',
    component: () => import('@/views/User/LeaderboardShow'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  /**
  * MENTORS
  */
  {
    path: '/mentors',
    name: 'MentorsIndex',
    components: {
      default: () => import('@/views/mentors/MentorsIndex'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true
    },
  },

  /**
   * Content Share
   */
  {
    path: '/content-share',
    name: 'ContentShareIndex',
    component: () => import('@/views/content-share/ContentShareList'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/campaign-content-share',
    name: 'CampaignContentShareIndex',
    components: {
      default: () => import('@/views/content-share/CampaignContentShareIndex'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/content-share/:shareCode',
    name: 'ContentShareShow',
    component: () => import('@/views/content-share/ShareShow'),
    // @TODO: this is a hotfix for broken links detected on 01/04/2022
    alias: [ '/c/:shareCode', '/e/c/:shareCode' ],
    props: true,
    meta: {
      requiresAuth: false,
      pageView: ({ shareCode }) => ({
        contentShare: { shareCode }
      }),
    },
  },
  {
    path: '/c/trackredirect/:shareCode/:url',
    name: 'ContentShareTrackRedirect',
    component: () => import('@/views/content-share/ContentShareTrackRedirect'),
    props: true,
    meta: {
      requiresAuth: false,
      pageView: ({ shareCode }) => ({
        contentShare: { shareCode }
      }),
    },
  },

  {
    path: '/rewards',
    name: 'RewardsIndex',
    components: {
      default: () => import('@/views/User/Rewards'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/rewards-browser',
    name: 'RewardsBrowser',
    components: {
      default: () => import('@/views/User/RewardsBrowser'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
    },
  },

  /**
  * Analytics
  */
  {
    path: '/manage/analytics',
    name: 'ManageAnalytics',
    components: {
      default: () => import('@/views/Recruiter/Stats'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    name: 'ManageAnalyticsReports',
    path: '/manage/analytics/reports',
    components: {
      default: () => import('@/views/Recruiter/StatsReports'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    path: '/all-referral-requests',
    name: 'ListReferrals',
    component: () => import('@/views/analytics/analyticsReferralRequests'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    path: '/all-applications',
    name: 'ListApplications',
    component: () => import('@/views/Recruiter/ListApplications'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    path: '/all-applications-new',
    redirect: '/all-applications',
  },
  {
    path: '/all-users',
    name: 'ListUsers',
    component: () => import('@/views/Recruiter/ListUsers'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    path: '/talentpool',
    name: 'TalentPool',
    component: () => import('@/views/Recruiter/TalentPool'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    path: '/settings/map-suggested-skills',
    name: 'MapSuggestedSkills',
    component: () => import('@/views/Recruiter/MapSuggestedSkills'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },

  {
    name: 'ManageApplicationImports',
    path: '/manage/applications/import',
    component: () => import('@/views/Recruiter/ApplicationImporter'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },

  // {
  //   name: 'ManageUsers',
  //   path: '/manage/users',
  //   component: () => import('@/views/Recruiter/UsersList'),
  //   props: true,
  //   meta: {
  //     requiresAuth: true,
  //     requiresRecruiter: true,
  //   },
  // },
  {
    name: 'EmailSender',
    path: '/manage/email-sender',
    component: () => import('@/views/Recruiter/EmailSender'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    name: 'ManageUserInvites',
    path: '/manage/users/invite',
    component: () => import('@/views/User/UsersInvite'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },

  {
    name: 'ManageLeaderboards',
    path: '/manage/leaderboards',
    component: () => import('@/views/Recruiter/ManageLeaderboards'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    name: 'ManageLeaderboard',
    path: '/manage/leaderboards/:leaderboardID',
    component: () => import('@/views/Recruiter/ManageLeaderboard'),
    props: true,
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },

  {
    name: 'ManageAll',
    path: '/manage',
    components: {
      default: () => import(/* webpackChunkName: "manage" */'@/views/manage/all'),
      layout: MainLayout,
    },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  ...(process.env.NODE_ENV === 'production' ? [] : [
    {
      name: 'ManageEntryInputs',
      path: '/manage/inputs',
      components: {
        default: () => import(/* webpackChunkName: "manage" */'@/views/manage/inputs'),
        layout: MainLayout,
      },
      meta: {
        requiresAuth: true,
        requiresRecruiter: true,
      },
    },
  ]),
  {
    name: 'ManageEntryList',
    path: '/manage/:entryType',
    components: {
      default: () => import(/* webpackChunkName: "manage" */'@/views/manage/list'),
      layout: MainLayout,
    },
    props: { default: true, layout: false },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    name: 'ManageEntryView',
    path: '/manage/:entryType/:entryID',
    components: {
      default: () => import(/* webpackChunkName: "manage" */'@/views/manage/view'),
      layout: MainLayout,
    },
    props: { default: true, layout: false },
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },

  {
    path: '/app',
    name: 'AppPromo',
    components: {
      default: () => import('@/views/User/AppPromo'),
      layout: MainLayout,
    },
  },
  {
    path: '/slack',
    name: 'SlackLanding',
    components: {
      default: () => import('@/views/User/SlackLanding'),
      layout: MainLayout,
    },
  },
  {
    path: '/teams',
    name: 'TeamsLanding',
    components: {
      default: () => import('@/views/User/TeamsLanding'),
      layout: MainLayout,
    },
  },
  {
    name: 'EmployeeHelp',
    path: '/help/employee',
    component: () => import('@/views/static/help/employee'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: 'RecruiterHelp',
    path: '/help/recruiter',
    component: () => import('@/views/static/help/recruiter'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    }
  },
  {
    name: 'RecruiterAdminHelp',
    path: '/help/recruiter-admin',
    component: () => import('@/views/static/help/recruiterAdmin'),
    meta: {
      requiresAuth: true,
      requiresRecruiter: true,
    },
  },
  {
    name: 'TrainingVideo',
    path: '/help/training',
    component: () => import('@/views/static/help/training'),
  },

  /* Client specific */
  {
    name: 'eXPD8-UG',
    path: '/expd8/user-guide',
    component: () => import('@/views/static/clients/eXPD8UG'),
  },
  {
    name: 'eXPD8-TC',
    path: '/expd8/tcs',
    component: () => import('@/views/static/clients/eXPD8TandCs'),
  },

  /**
   * ENGAGEMENT
   */
  {
    path: '/engagement/sharejob/:jobIDs',
    name: 'EngagementShareJob',
    components: {
      default: () => import('@/views/engagement/ShareJob'),
      layout: MainLayout,
    },
    props: { default: true, layout: false },
    meta: {
      requiresAuth: true,
      pageView: ({ jobIDs }) => ({
        job: { id: jobIDs.split(',')[0] },
      }),
    },
  },
  {
    path: '/engagement/sharecontent/:contentShareIDs',
    name: 'EngagementShareContent',
    components: {
      default: () => import('@/views/engagement/ShareContent'),
      layout: MainLayout,
    },
    props: { default: true, layout: false },
    meta: {
      requiresAuth: true,
      pageView: ({ contentShareIDs }) => ({
        contentShare: { id: contentShareIDs.split(',')[0] },
      }),
    },
  },
  {
    path: '/engagement/viewgamification/:campaignID',
    name: 'EngagementViewGamification',
    components: {
      default: () => import('@/views/engagement/ViewGamification'),
      layout: MainLayout,
    },
    props: { default: true, layout: false },
    meta: {
      requiresAuth: true,
      pageView: ({ campaignID }) => ({
        campaign: { id: campaignID },
      }),
    },
  },

  trackingRoute,
  { name: 'Error404', path: '*', components: { default: ErrorPage, layout: MainLayout } },
];

export function createRouter() {
  return new Router({
    // base,
    mode: 'history',
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else if (to.hash && !(to.meta && to.meta.scrollBehavior === false)) {
        // Don't jump-to-hash if the page doesn't support this behavior
        return {
          selector: to.hash
          // , offset: { x: 0, y: 10 }
        };
      } else {
        return { x: 0, y: 0 };
      }
    },
    routes,
  });
}



// WEBPACK FOOTER //
// ./src/router/platform.js